export type CellValue = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export type Board = readonly CellValue[];

export const BOARD_SIZE = 81;
export const UNIT_SIZE = 9;
export const ALL_CANDIDATES_MASK = 0b11_1111_1110;

function isCellValue(value: number): value is CellValue {
  return Number.isInteger(value) && value >= 0 && value <= UNIT_SIZE;
}

export function parseBoard(serialized: string): Board {
  if (serialized.length !== BOARD_SIZE) {
    throw new Error('A Sudoku board must contain exactly 81 cells.');
  }

  const values = Array.from(serialized, (character) => Number(character));
  if (!values.every(isCellValue)) {
    throw new Error('A Sudoku board may only contain digits from 0 to 9.');
  }
  return values;
}

export function boardFromValues(values: readonly number[]): Board {
  if (values.length !== BOARD_SIZE || !values.every(isCellValue)) {
    throw new Error('A Sudoku board must contain 81 values from 0 to 9.');
  }
  return [...values];
}

export function rowOf(index: number): number {
  return Math.floor(index / UNIT_SIZE);
}

export function columnOf(index: number): number {
  return index % UNIT_SIZE;
}

export function boxOf(index: number): number {
  return Math.floor(rowOf(index) / 3) * 3 + Math.floor(columnOf(index) / 3);
}

export function peerIndexes(index: number): readonly number[] {
  if (!Number.isInteger(index) || index < 0 || index >= BOARD_SIZE) {
    return [];
  }

  const peers = new Set<number>();
  const row = rowOf(index);
  const column = columnOf(index);
  const boxRow = Math.floor(row / 3) * 3;
  const boxColumn = Math.floor(column / 3) * 3;

  for (let offset = 0; offset < UNIT_SIZE; offset += 1) {
    peers.add(row * UNIT_SIZE + offset);
    peers.add(offset * UNIT_SIZE + column);
    peers.add((boxRow + Math.floor(offset / 3)) * UNIT_SIZE + boxColumn + (offset % 3));
  }
  peers.delete(index);
  return [...peers].sort((left, right) => left - right);
}

export function candidateMask(board: Board, index: number): number {
  if (board[index] !== 0) {
    return 0;
  }

  let usedMask = 0;
  for (const peer of peerIndexes(index)) {
    const value = board[peer] ?? 0;
    usedMask |= 1 << value;
  }
  return ALL_CANDIDATES_MASK & ~usedMask;
}

function addDuplicateIndexes(board: Board, indexes: readonly number[], result: Set<number>): void {
  const firstByValue = new Map<number, number>();
  for (const index of indexes) {
    const value = board[index] ?? 0;
    if (value === 0) {
      continue;
    }
    const first = firstByValue.get(value);
    if (first === undefined) {
      firstByValue.set(value, index);
    } else {
      result.add(first);
      result.add(index);
    }
  }
}

export function conflictingCells(board: Board): ReadonlySet<number> {
  const conflicts = new Set<number>();
  for (let unit = 0; unit < UNIT_SIZE; unit += 1) {
    const row = Array.from({ length: UNIT_SIZE }, (_, offset) => unit * UNIT_SIZE + offset);
    const column = Array.from({ length: UNIT_SIZE }, (_, offset) => offset * UNIT_SIZE + unit);
    const boxRow = Math.floor(unit / 3) * 3;
    const boxColumn = (unit % 3) * 3;
    const box = Array.from(
      { length: UNIT_SIZE },
      (_, offset) => (boxRow + Math.floor(offset / 3)) * UNIT_SIZE + boxColumn + (offset % 3),
    );
    addDuplicateIndexes(board, row, conflicts);
    addDuplicateIndexes(board, column, conflicts);
    addDuplicateIndexes(board, box, conflicts);
  }
  return conflicts;
}

export function isComplete(board: Board): boolean {
  return board.every((value) => value !== 0) && conflictingCells(board).size === 0;
}

export function composeBoard(puzzle: Board, entries: Board): Board {
  return boardFromValues(
    puzzle.map((value, index) => (value === 0 ? (entries[index] ?? 0) : value)),
  );
}

export function maskValues(mask: number): readonly CellValue[] {
  const values: CellValue[] = [];
  for (let value = 1; value <= UNIT_SIZE; value += 1) {
    if ((mask & (1 << value)) !== 0 && isCellValue(value)) {
      values.push(value);
    }
  }
  return values;
}
